/**
 * Property Database API
 * 
 * Functions for the deduplicated property database.
 * This is separate from properties.ts which handles per-run scraped properties.
 */

import { supabase } from '@/integrations/supabase/client';

// ============================================================
// Types
// ============================================================

export interface Property {
  id: string;
  address_key: string;
  full_address: string;
  suburb: string;
  postcode: string;
  state: string;
  property_type: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parking: number | null;
  property_size: string | null;
  property_size_unit: string | null;
  price: number | null;
  price_display: string | null;
  listing_type: string | null;
  last_sale_date: string | null;
  last_sale_price: number | null;
  description: string | null;
  agency_name: string | null;
  source_urls: string[];
  source_adapters: string[];
  first_seen_at: string;
  last_scraped_at: string | null;
  scrape_count: number;
  snapshot_count: number;
  raw_data: Record<string, unknown> | null;
  place_id: string | null;
  latitude: number | null;
  longitude: number | null;
  geocoded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PropertySnapshot {
  id: string;
  property_id: string;
  job_id: string | null;
  job_run_id: string | null;
  queue_item_id: string | null;
  source_url: string;
  adapter_slug: string | null;
  listing_type: string | null;
  sale_date: string | null;
  sale_price: number | null;
  asking_price: number | null;
  price_display: string | null;
  property_type: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parking: number | null;
  property_size: string | null;
  property_size_unit: string | null;
  land_size: string | null;
  land_size_unit: string | null;
  agency_name: string | null;
  description: string | null;
  raw_data: Record<string, unknown> | null;
  snapshot_date: string;
  created_at: string;
  page_snapshot_id?: string | null;
  page_snapshot?: {
    id: string;
    provider: string;
    storage_bucket: string;
    storage_path: string;
    content_type: string | null;
    content_encoding: string | null;
    captured_at: string;
  } | null;
  job?: {
    id: string;
    name: string;
  } | null;
  job_run?: {
    id: string;
    status: string;
    started_at: string;
    completed_at: string | null;
  } | null;
}

export interface PropertyFilters {
  search?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  postcodes?: string[];
  propertyType?: string;
  listingType?: string;
  minPrice?: number;
  maxPrice?: number;
  minBedrooms?: number;
}

export interface PropertyStats {
  total: number;
  uniqueSuburbs: number;
  uniqueStates: number;
  withPrice: number;
  avgPrice: number | null;
}

// ============================================================
// Normalization Maps
// ============================================================

/**
 * Canonical property type categories.
 * Maps lowercase raw DB values to a canonical label.
 * Values not found in this map are shown as-is (title-cased).
 */
const PROPERTY_TYPE_MAP: Record<string, string> = {
  'house': 'House',
  'single level home': 'House',
  'single-level sanctuary': 'House',
  'single-level victorian': 'House',
  'victorian cottage': 'House',
  'victorian home': 'House',
  'victorian residence': 'House',
  'luxury 3-bedroom home': 'House',
  'family lifestyle retreat': 'House',
  'cottage': 'House',
  'apartment': 'Apartment',
  'apartment / unit / flat': 'Apartment',
  'apartment / off the plan': 'Apartment',
  'apartments': 'Apartment',
  'new apartments / off the plan': 'Apartment',
  'unit': 'Apartment',
  'flat': 'Apartment',
  'studio': 'Studio',
  'studio apartment': 'Studio',
  'townhouse': 'Townhouse',
  'town residence': 'Townhouse',
  'terrace': 'Townhouse',
  'semi-detached': 'Semi-Detached',
  'duplex': 'Semi-Detached',
  'villa': 'Villa',
  'villa unit': 'Villa',
  'penthouse': 'Penthouse',
  'penthouse apartment': 'Penthouse',
  'penthouse/apartment/unit/flat': 'Penthouse',
  'sub-penthouse': 'Penthouse',
  'vacant land': 'Land',
  'land': 'Land',
  'house & land': 'Land',
  'development site': 'Land',
  'residential': 'Residential',
  'block of units': 'Block of Units',
  'retirement living': 'Retirement',
  'retirement': 'Retirement',
  'commercial': 'Commercial',
  'commercial/residential': 'Commercial',
  'residential/commercial': 'Commercial',
  'mixed use': 'Commercial',
  'office': 'Commercial',
  'office/showroom/warehouse': 'Commercial',
  'retail shop': 'Commercial',
  'retail tenancy': 'Commercial',
  'shop & dwelling': 'Commercial',
  'warehouse style property': 'Commercial',
  'car space': 'Other',
  'farm & vineyard': 'Rural',
  'for sale': 'Other',
};

/** Ordered list of canonical property types for the dropdown */
const CANONICAL_PROPERTY_TYPES = [
  'House',
  'Apartment',
  'Townhouse',
  'Villa',
  'Semi-Detached',
  'Penthouse',
  'Studio',
  'Land',
  'Block of Units',
  'Residential',
  'Retirement',
  'Commercial',
  'Rural',
  'Other',
] as const;

/**
 * Normalize a raw property_type string to its canonical label.
 */
export function normalizePropertyType(raw: string): string {
  return PROPERTY_TYPE_MAP[raw.toLowerCase()] ?? raw;
}

/** Friendly labels for listing type values stored in the DB */
export const LISTING_TYPE_LABELS: Record<string, string> = {
  buy: 'For Sale',
  sold: 'Sold',
  rent: 'For Rent',
  auction: 'Auction',
};

function sanitizeSearchTerm(value: string): string {
  return value
    .replace(/[%(),'"\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildFlexibleIlikePattern(value: string): string {
  const tokens = value.split(/\s+/).filter(Boolean);
  return `%${tokens.join('%')}%`;
}

// ============================================================
// Property Database Operations
// ============================================================

/**
 * Fetch properties with pagination and filters.
 *
 * `propertyType` may be a canonical label (e.g. "House") which is
 * mapped back to all matching raw DB values via ilike patterns.
 */
export async function fetchProperties(options: {
  filters?: PropertyFilters;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
} = {}): Promise<{ data: Property[]; count: number }> {
  const { filters = {}, limit = 50, offset = 0, sortBy = 'last_scraped_at', sortOrder = 'desc' } = options;

  let query = (supabase as any)
    .from('properties')
    .select('*', { count: 'exact' });

  // Apply filters
  if (filters.search) {
    const safeSearchTerm = sanitizeSearchTerm(filters.search);
    if (safeSearchTerm) {
      const searchPattern = buildFlexibleIlikePattern(safeSearchTerm);
      query = query.or(
        `full_address.ilike.${searchPattern},suburb.ilike.${searchPattern},description.ilike.${searchPattern}`,
      );
    }
  }
  if (filters.suburb) {
    query = query.ilike('suburb', `%${filters.suburb}%`);
  }
  if (filters.state && filters.state !== 'all') {
    query = query.eq('state', filters.state);
  }
  if (filters.postcodes && filters.postcodes.length > 0) {
    query = query.in('postcode', filters.postcodes);
  } else if (filters.postcode) {
    query = query.eq('postcode', filters.postcode);
  }
  if (filters.propertyType && filters.propertyType !== 'all') {
    // Map canonical label back to all raw DB values that belong to this category
    const rawKeys = Object.entries(PROPERTY_TYPE_MAP)
      .filter(([, canonical]) => canonical === filters.propertyType)
      .map(([raw]) => raw);

    if (rawKeys.length > 0) {
      // Build an OR filter matching all raw variants (case-insensitive)
      const orClauses = rawKeys.map((raw) => `property_type.ilike.${raw}`).join(',');
      query = query.or(orClauses);
    } else {
      // No mapping found — try exact case-insensitive match
      query = query.ilike('property_type', filters.propertyType);
    }
  }
  if (filters.listingType && filters.listingType !== 'all') {
    query = query.eq('listing_type', filters.listingType);
  }
  if (filters.minPrice) {
    query = query.gte('price', filters.minPrice);
  }
  if (filters.maxPrice) {
    query = query.lte('price', filters.maxPrice);
  }
  if (filters.minBedrooms) {
    query = query.gte('bedrooms', filters.minBedrooms);
  }

  // Apply sorting and pagination
  query = query
    .order(sortBy, { ascending: sortOrder === 'asc' })
    .range(offset, offset + limit - 1);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching properties:', error);
    throw error;
  }

  return { data: data || [], count: count || 0 };
}

/**
 * Get property database stats
 */
export async function getPropertyStats(): Promise<PropertyStats> {
  const [totalResult, suburbResult, stateResult, priceResult, avgResult] = await Promise.all([
    (supabase as any).from('properties').select('*', { count: 'exact', head: true }),
    (supabase as any).rpc('count_distinct_suburbs'),
    (supabase as any).rpc('count_distinct_states'),
    (supabase as any).from('properties').select('*', { count: 'exact', head: true }).not('price', 'is', null),
    (supabase as any).from('properties').select('price').not('price', 'is', null),
  ]);

  // Calculate average price client-side if RPC not available
  let avgPrice: number | null = null;
  if (avgResult.data && avgResult.data.length > 0) {
    const prices = avgResult.data.map((r: { price: number }) => r.price).filter(Boolean);
    if (prices.length > 0) {
      avgPrice = Math.round(prices.reduce((a: number, b: number) => a + b, 0) / prices.length);
    }
  }

  return {
    total: totalResult.count || 0,
    uniqueSuburbs: suburbResult.data || 0,
    uniqueStates: stateResult.data || 0,
    withPrice: priceResult.count || 0,
    avgPrice,
  };
}

/**
 * Get simple property stats using count queries (no RPC needed)
 */
export async function getPropertyStatsSimple(): Promise<{
  total: number;
  withPrice: number;
}> {
  const [totalResult, priceResult] = await Promise.all([
    (supabase as any).from('properties').select('*', { count: 'exact', head: true }),
    (supabase as any).from('properties').select('*', { count: 'exact', head: true }).not('price', 'is', null),
  ]);

  return {
    total: totalResult.count || 0,
    withPrice: priceResult.count || 0,
  };
}

/**
 * Get canonical property types for filter dropdowns.
 *
 * Returns the predefined canonical categories. This avoids querying
 * thousands of rows from the DB just to derive dropdown labels.
 * The normalization map covers all known raw DB values.
 */
export function getPropertyTypes(): string[] {
  return [...CANONICAL_PROPERTY_TYPES];
}

/**
 * Get listing type options for the filter dropdown.
 * Returns objects with { value, label } for display.
 *
 * Uses a predefined set of known listing types rather than
 * querying all rows (Supabase default row limit is 1000 which
 * caused incomplete results).
 */
export function getListingTypes(): { value: string; label: string }[] {
  const knownTypes = ['buy', 'sold', 'rent', 'auction'] as const;
  return knownTypes.map((value) => ({
    value,
    label: LISTING_TYPE_LABELS[value] ?? value,
  }));
}

/**
 * Fetch snapshots (historical records) for a specific property
 */
export async function fetchPropertySnapshots(propertyId: string): Promise<PropertySnapshot[]> {
  const rows: any[] = [];
  const pageSize = 500;
  for (let offset = 0; ; offset += pageSize) {
    const { data, error } = await (supabase as any)
      .from('property_snapshots')
      .select(`
        *,
        page_snapshots!property_snapshots_page_snapshot_id_fkey(
          id,
          provider,
          storage_bucket,
          storage_path,
          content_type,
          content_encoding,
          captured_at
        ),
        scrape_jobs!property_snapshots_job_id_fkey(
          id,
          name
        ),
        job_runs!property_snapshots_job_run_id_fkey(
          id,
          status,
          started_at,
          completed_at
        )
      `)
      .eq('property_id', propertyId)
      .order('snapshot_date', { ascending: false })
      .order('id', { ascending: false })
      .range(offset, offset + pageSize - 1);

    if (error) {
      console.error('Error fetching property snapshots:', error);
      throw error;
    }
    rows.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }

  return rows.map((row: any) => ({
    ...row,
    page_snapshot: row.page_snapshots || null,
    job: row.scrape_jobs || null,
    job_run: row.job_runs || null,
  }));
}

/**
 * Fetch a single property by id.
 *
 * Returns null if no row matches the provided id.
 */
export async function fetchPropertyById(id: string): Promise<Property | null> {
  const { data, error } = await (supabase as any)
    .from("properties")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    console.error("Error fetching property by id:", error);
    throw error;
  }

  return (data as Property | null) ?? null;
}

// ============================================================
// Suburb Detail Aggregation
// ============================================================

export interface SuburbDetailStats {
  locality: string;
  state: string | null;
  postcode: string | null;
  propertyCount: number;
  withPriceCount: number;
  soldCount: number;
  sold30: number;
  sold60: number;
  sold90: number;
  medianSalePrice: number | null;
  avgSnapshotsPerProperty: number;
  totalSnapshots: number;
  sourceAdapters: string[];
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? Math.round((sorted[mid - 1] + sorted[mid]) / 2) : sorted[mid];
}

/**
 * Fetch suburb detail: stats + the deduplicated properties that belong to it.
 *
 * `locality` is matched case-insensitively against the `suburb` column on
 * `properties`. We deliberately scan the properties table directly (rather
 * than joining via the `suburbs` reference) because property records may
 * have come from adapters that record the suburb name without a stable id.
 */
export async function fetchSuburbDetail(
  locality: string,
  options: { propertyLimit?: number } = {},
): Promise<{ stats: SuburbDetailStats; properties: Property[] }> {
  const { propertyLimit = 500 } = options;

  const { data, error, count } = await (supabase as any)
    .from("properties")
    .select("*", { count: "exact" })
    .ilike("suburb", locality)
    .order("last_scraped_at", { ascending: false, nullsFirst: false })
    .limit(propertyLimit);

  if (error) {
    console.error("Error fetching suburb detail:", error);
    throw error;
  }

  const properties = (data || []) as Property[];
  const now = Date.now();
  const days = (ms: number) => ms / (1000 * 60 * 60 * 24);

  let sold30 = 0;
  let sold60 = 0;
  let sold90 = 0;
  let soldCount = 0;
  let totalSnapshots = 0;
  let withPriceCount = 0;
  const salePrices: number[] = [];
  const adapterSet = new Set<string>();
  let state: string | null = null;
  let postcode: string | null = null;

  for (const p of properties) {
    if (p.state && !state) state = p.state;
    if (p.postcode && !postcode) postcode = p.postcode;
    if (p.last_sale_price) {
      withPriceCount++;
      salePrices.push(p.last_sale_price);
    } else if (p.price) {
      withPriceCount++;
    }
    if (p.listing_type?.toLowerCase() === "sold") soldCount++;
    if (p.last_sale_date) {
      const ageDays = days(now - new Date(p.last_sale_date).getTime());
      if (ageDays <= 30) sold30++;
      if (ageDays <= 60) sold60++;
      if (ageDays <= 90) sold90++;
    }
    totalSnapshots += p.snapshot_count ?? 0;
    for (const a of p.source_adapters ?? []) adapterSet.add(a);
  }

  const propertyCount = count ?? properties.length;
  const stats: SuburbDetailStats = {
    locality,
    state,
    postcode,
    propertyCount,
    withPriceCount,
    soldCount,
    sold30,
    sold60,
    sold90,
    medianSalePrice: median(salePrices),
    avgSnapshotsPerProperty:
      properties.length > 0 ? Math.round((totalSnapshots / properties.length) * 10) / 10 : 0,
    totalSnapshots,
    sourceAdapters: [...adapterSet].sort(),
  };

  return { stats, properties };
}

/**
 * Get unique suburbs for filter dropdowns
 */
export async function getPropertySuburbs(): Promise<string[]> {
  const { data, error } = await (supabase as any)
    .from('properties')
    .select('suburb')
    .order('suburb');

  if (error) {
    console.error('Error fetching suburbs:', error);
    return [];
  }

  return [...new Set((data || []).map((r: { suburb: string }) => r.suburb))].filter(Boolean) as string[];
}
